import caseImage from "assets/hong-kong-dial-ii/case.jpg";
import galleryImage1 from "assets/hong-kong-dial-ii/gallery/1.jpg";
import galleryImage2 from "assets/hong-kong-dial-ii/gallery/2.jpg";
import galleryImage3 from "assets/hong-kong-dial-ii/gallery/3.jpg";
import galleryImage4 from "assets/hong-kong-dial-ii/gallery/4.jpg";
import galleryImage5 from "assets/hong-kong-dial-ii/gallery/5.jpg";
import galleryImage6 from "assets/hong-kong-dial-ii/gallery/6.jpg";
import originImage from "assets/hong-kong-dial-ii/origin.jpg";
import quartzImage from "assets/hong-kong-dial-ii/quartz.jpg";
import watchfaceImage from "assets/hong-kong-dial-ii/watchface.png";
import watchfacesImage from "assets/hong-kong-dial-ii/watchfaces.jpg";
import Head from "next/head";
import Image from "next/image";

import Footer from "components/hong-kong-dial-ii/Footer";
import TAImage from "components/hong-kong-dial-ii/Image";
import ImageCarousel from "components/hong-kong-dial-ii/ImageCarousel";
import Spacer from "components/hong-kong-dial-ii/Spacer";
import Text from "components/hong-kong-dial-ii/Text";
import NoBreak from "components/hong-kong-dial-ii/NoBreak";

import Logos from "../../assets/hong-kong-dial-ii/logos.svg";
import { LogosWrapper, Masthead, Root } from "./hong-kong-dial-ii.style";

const metaDescription =
	"Discover the exclusive Hong Kong Dial II at The Armoury. A perfect blend of timeless design and precision craftsmanship, this unique timepiece celebrates Hong Kong’s vibrant heritage. Shop now for a refined addition to your watch collection.";
const description =
	"Introducing the Hong Kong Dial II by The Armoury – a stunning tribute to Hong Kong’s culture and elegance. Crafted with precision and designed for the modern wearer. Explore this exceptional timepiece now.";

export default function Page() {
	return (
		<>
			<Head>
				<title>Hong Kong Dial II - The Armoury</title>
				<meta name="description" content={metaDescription} />
				{/* Facebook Meta Tags */}
				<meta
					property="og:url"
					content="https://www.thearmoury.com/watches/hong-kong-dial"
				/>
				<meta property="og:type" content="website" />
				<meta property="og:title" content="Hong Kong Dial II" />
				<meta property="og:description" content={description} />
				{/* Twitter Meta Tags */}
				<meta name="twitter:card" content="summary_large_image" />
				<meta property="twitter:domain" content="watches.thearmoury.com" />
				<meta
					property="twitter:url"
					content="https://www.thearmoury.com/watches/hong-kong-dial-ii"
				/>
				<meta name="twitter:title" content="Hong Kong Dial II" />
				<meta name="twitter:description" content={description} />

				<link
					rel="icon"
					type="image/png"
					href="/favicon-32x32.png"
					sizes="32x32"
				/>
				<link
					rel="icon"
					type="image/png"
					href="/favicon-16x16.png"
					sizes="16x16"
				/>
				<link rel="manifest" type="image/png" href="/manifest.json" />
				<link rel="mast-icon" href="/safari-pinned-tab.svg" />
				<link rel="shortcut icon" href="/favicon.ico" />
				<link rel="stylesheet" href="https://use.typekit.net/dbw1jpz.css" />
			</Head>

			<Root>
				<Masthead>
					<Image src={watchfaceImage} alt="" width={872} height={842} />
					<LogosWrapper>
						<Logos />
					</LogosWrapper>
					<Text header="The Hong Kong Dial II">
						<p>
							Building on the success of the original Hong Kong Dial, The
							Armoury is proud to introduce the Hong Kong Dial II, a refined
							evolution of this distinctive timepiece. Inspired by our journey
							through Scotland, the Hong Kong Dial II retains the signature 35mm
							1970s-style C-shaped case that first captured our imagination in
							Glasgow. This new iteration continues to celebrate The Armoury’s
							unique blend of Eastern and Western influences, now with subtle
							design updates that enhance its timeless appeal while staying true
							to the charm of the original California Dial-inspired layout.
						</p>
					</Text>
					<TAImage src={originImage} />
					<Text header="The Origin">
						<p>
							The origins of the dial’s design actually hark back to Rolex’s
							earliest marketing of this style of dial which was known as
							“Error-Proof” but for various reasons is better known as a
							“California Dial”. These dials emphasized clear legibility using
							heavy graphics, such as the triangle at 12, and fonts, which
							matched well with Paulin’s bold graphic style. We adapted Chinese
							characters to suit these characteristics and were especially
							pleased to be able to use Chinese for the bottom half of the dial
							as 4, 5, 8 and 9 are visually the most interesting of the 1 - 12
							required on a dial.
						</p>
						<p>
							For more info about the “Error-Proof” and “California Dial”
							history, please see -{" "}
							<a href="https://revolutionwatch.com/california-love/">
								https://revolutionwatch.com/california-love/
							</a>
						</p>
					</Text>
				</Masthead>
				<TAImage src={watchfacesImage} flush={true} />
				<Text header="Quartz!?">
					<p>
						We have a soft spot for thin, center-hand watches and one of the
						most economical and effective ways to achieve this is by using a
						quartz movement, which can be made thinner than a traditional manual
						wind movement. Furthermore, a quartz movement is convenient. Instead
						of having to wind the movement or re-setting the time when the watch
						has stopped, you simply change the battery every few years.
					</p>
				</Text>
				<TAImage src={quartzImage} />
				<Text header="Case Design">
					<p>
						We love working with the people at Paulin and anOrdain. Unusually
						for a watch company, they make little reference to classic watch
						design and instead progress in their own very distinct and quirky
						way with a heavy emphasis on graphic elements.
					</p>
					<p>
						Their case design reflects their pragmatic, function-led approach to
						watchmaking. The black ring that surrounds the dial of the watch is
						a modular spacer that allows them to adapt the case for different
						sizes of movements while also adding an interesting design detail.
					</p>
				</Text>
				<TAImage src={caseImage} />
				<Text header="Color — How to wear it">
					<p>
						The Hong Kong Dial II introduces a versatile array of dial colors—
						<NoBreak>viridian green</NoBreak>, <NoBreak>dusty blue</NoBreak>,{" "}
						<NoBreak>radiant burgundy</NoBreak> and{" "}
						<NoBreak>white silver</NoBreak>—each offering unique styling
						opportunities. The <NoBreak>dusty blue</NoBreak> dial is a
						sophisticated match for a crisp blue shirt and dark blazer. The{" "}
						<NoBreak>white silver</NoBreak> dial, with its understated elegance,
						complements neutral tones and pairs effortlessly with a linen suit
						or a minimalist ensemble. The <NoBreak>viridian green</NoBreak> dial
						brings a rich, earthy vibrancy that works beautifully with casual
						outfits, while the <NoBreak>radiant burgundy</NoBreak> dial adds a
						bold touch, ideal for accentuating warm hues in your wardrobe.
					</p>
				</Text>
				<ImageCarousel
					images={[
						galleryImage1,
						galleryImage2,
						galleryImage3,
						galleryImage4,
						galleryImage5,
						galleryImage6,
					]}
				/>
				<Spacer />
				<Footer />
			</Root>
		</>
	);
}

Page.getLayout = function getLayout(page) {
	return <div className="overflow-x-hidden">{page}</div>;
};
